import React, {FC, useEffect, useMemo, useState} from 'react';
import {Box} from '@material-ui/core';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {FilterIcon} from '@epic-ui/icons';

import {Widget} from '../../../../common/widget';
import RewardsBarChart from '../components/chart/RewardsBarChart';
import HorizontalLabel from '../components/HorizontalLabel';
import RadioGameSelect from '../../../../radio-game-select/RadioGameSelect';
import RewardsDatePicker from './RewardsDatePicker';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import IconButton from '../../../../common/IconButton';
import useChartTrends from '../hooks/useChartTrends';
import useLocalization from '../../../../../hooks/useLocalization';
import {PDFPictureTrap, PDF_CONTAINER_WIDTH} from '../export/PDFExportProvider';
import useRewardsData from './useRewardsData';
import {prepareLineChartExportData} from '../exportUtils';
import {useRangePickerData} from '../../../../../components/common/date-range-picker';
import IntervalRadioGroupPlaceholder from '../components/interval-radio-group/IntervalRadioGroupPlaceholder';
import IntervalSelectPlaceholder from '../components/interval-radio-group/IntervalSelectPlaceholder';
import {REWARDS_PRESELECTED_DATE_RANGE} from '../constants';

import {WidgetProps} from '../types';
import {Status, DateRange, TimeScale, Payout, Option} from '../../../../../types';

import {OptionWrapper, ChartWrapper} from '../styles';

const CODES = {
    showMe: 'epic.affiliate.fortnite_games.show_me',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    title: 'epic.affiliate.fortnite_games.rewards.title',
    tooltip: 'epic.affiliate.fortnite_games.rewards.tooltip',
    payout: 'epic.affiliate.fortnite_games.rewards.payout.',
    combined: 'epic.affiliate.fortnite_games.rewards.combined',
    timeScaleTooltip: 'epic.affiliate.fortnite_games.rewards.time_scale.tooltip'
};

const RewardsWidget: FC<WidgetProps> = ({gamesList, keysMap, gamesLoading}: WidgetProps) => {
    const getMessage = useLocalization();

    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const isXlBreakpoint = useMediaQuery(theme.breakpoints.up('xl'));
    const combinedOption: Option = {value: 'all', label: getMessage(CODES.combined)};
    const [game, setGame] = useState(combinedOption);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const pageFormat = isMobileMenuOpen ? [PDF_CONTAINER_WIDTH, 800] : [PDF_CONTAINER_WIDTH, 670];

    const timeScaleOptions = useMemo(() => {
        return Object.values(TimeScale)
            .slice(2)
            .map(value => ({
                label: getMessage(`${CODES.timeScale}${value}`),
                value
            }));
    }, []);

    const payoutOptions = useMemo(() => {
        return Object.values(Payout).map(value => ({
            label: getMessage(`${CODES.payout}${value}`),
            value
        }));
    }, []);

    const timeScale = timeScaleOptions[2];

    const islandCode = game.value !== combinedOption.value && String(game.value);
    const selectedGames = islandCode ? payoutOptions.slice(0, 1) : payoutOptions;

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        REWARDS_PRESELECTED_DATE_RANGE
    );

    const {data, loading, error, reload} = useRewardsData({
        islandCode,
        startDate,
        endDate
    });

    const trends = useChartTrends({data, xAxis: 'time'});

    const isLoading = loading || gamesLoading;

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
    };

    const handleGameChange = (_event, gameOption) => {
        setGame(gameOption);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <RadioGameSelect
            options={gamesList}
            combinedOption={combinedOption}
            value={game}
            onChange={handleGameChange}
        />
    );

    const TimeSelect = (
        <IntervalSelectPlaceholder tooltip={getMessage(CODES.timeScaleTooltip)} value={timeScale} />
    );

    const DatePicker = (
        <RewardsDatePicker
            timeScale={timeScale.value}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={1}>
            <Widget
                height={548}
                header={getMessage(CODES.title)}
                padding="15px"
                tooltip={getMessage(CODES.tooltip)}
                status={error ? Status.ERROR : undefined}
                message={getMessage(error) || error}
                overflow="visible"
                retry={reload}
                headerContent={
                    isFmdBreakpoint && (
                        <Box mt={1} width="460px">
                            {GameSelect}
                        </Box>
                    )
                }
                actions={
                    <>
                        {isFmdBreakpoint ? (
                            <>
                                {isXlBreakpoint ? (
                                    <HorizontalLabel>
                                        {getMessage(CODES.showMe)}
                                        <IntervalRadioGroupPlaceholder
                                            tooltip={getMessage(CODES.timeScaleTooltip)}
                                            options={timeScaleOptions}
                                            value={timeScale}
                                        />
                                    </HorizontalLabel>
                                ) : (
                                    <Box mx={1.5} minWidth={140}>
                                        {TimeSelect}
                                    </Box>
                                )}
                                <Box mx={1.5} width={233}>
                                    {DatePicker}
                                </Box>
                            </>
                        ) : (
                            <IconButton onClick={handleMobileMenuClick} isOpen={isMobileMenuOpen}>
                                <FilterIcon />
                            </IconButton>
                        )}
                        <WidgetDownloadButton
                            areaId={CODES.title}
                            fileName={getMessage(CODES.title)}
                            pageOptions={{orientation: 'l', pageFormat}}
                            data={data}
                            prepareCSVData={prepareLineChartExportData(keysMap)}
                            disabled={isLoading || !!error}
                        />
                    </>
                }
                options={
                    isMobileMenuOpen &&
                    !isFmdBreakpoint && (
                        <OptionWrapper>
                            {GameSelect}
                            {TimeSelect}
                            {DatePicker}
                        </OptionWrapper>
                    )
                }>
                <ChartWrapper>
                    <RewardsBarChart
                        selectedGames={selectedGames}
                        height={455}
                        data={data}
                        loading={isLoading}
                        keysMap={keysMap}
                        trends={trends}
                    />
                </ChartWrapper>
            </Widget>
        </PDFPictureTrap>
    );
};

export default RewardsWidget;
